import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Eye, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';

import Pagination from '../../../components/ui/pagination/Pagination';
import { useConfirm } from '../../../components/ui/confirm/ConfirmContext';
import { useToast } from '../../../components/ui/toast/ToastContext';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import AccountModal from './accountManagement/AccountModal';
import { areaLabels, formatDocument, jobTitleLabels } from './accountManagement/accountConstants';

const statusLabels = {
  active: 'Ativos',
  inactive: 'Inativos',
};

const initialFilters = {
  search: '',
  area: '',
  jobTitle: '',
  status: '',
};

const initialPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  perPage: 10,
};

const FilterSelect = ({ label, allLabel, value, options, onChange }) => {
  const selectedOption = options.find((option) => option.value === value);

  return (
    <label
      style={{ fontSize: '13px' }}
      className="relative flex h-10 min-w-max cursor-pointer items-center gap-1 rounded-full border border-bluePale bg-white px-4 text-blueSteel transition-colors hover:border-tealSoft"
    >
      <span className="font-semibold text-blueDark">{label}:</span>
      <span>{selectedOption?.label || allLabel}</span>
      <ChevronDown className="ml-1 text-blueSteel" size={16} strokeWidth={1.7} />
      <select
        aria-label={`Filtrar por ${label.toLowerCase()}`}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
};

FilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  allLabel: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

const StatusToggle = ({ active, disabled, employeeName, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    aria-label={`${active ? 'Inativar' : 'Ativar'} conta de ${employeeName}`}
    disabled={disabled}
    onClick={() => onChange(!active)}
    className={`relative flex-none rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-tealSoft focus:ring-offset-2 ${
      active ? 'bg-tealMedium' : 'bg-tealExtraDark3'
    } ${disabled ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
    style={{ width: '40px', height: '22px' }}
  >
    <span
      className="absolute rounded-full bg-white shadow-sm transition-transform"
      style={{ width: '16px', height: '16px', left: '3px', top: '3px', transform: `translateX(${active ? 18 : 0}px)` }}
    />
  </button>
);

StatusToggle.propTypes = {
  active: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  employeeName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const AccountManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [areas, setAreas] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [accessProfiles, setAccessProfiles] = useState([]);
  const [accountModal, setAccountModal] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(initialPagination);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [deletingIds, setDeletingIds] = useState(new Set());
  const hasLoaded = useRef(false);
  const { addToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 350);
    return () => window.clearTimeout(timeoutId);
  }, [filters.search]);

  useEffect(() => {
    const controller = new AbortController();

    const loadEmployees = async () => {
      if (hasLoaded.current) setFiltering(true);
      else setLoading(true);

      const query = new URLSearchParams();
      if (debouncedSearch) query.set('search', debouncedSearch);
      if (filters.area) query.set('area', filters.area);
      if (filters.jobTitle) query.set('job_title', filters.jobTitle);
      if (filters.status) query.set('status', filters.status);
      query.set('page', page);

      try {
        const endpoint = `/app/master/employees${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        const payload = camelizeKeys(await response.json());

        if (!response.ok) throw new Error(payload.errors?.join(' ') || 'Não foi possível carregar as contas.');

        setEmployees(payload.employees || []);
        setAreas(payload.areas || []);
        setJobTitles(payload.jobTitles || []);
        setStatuses(payload.statuses || []);
        setAccessProfiles(payload.accessProfiles || []);
        setPagination(payload.pagination || initialPagination);
        hasLoaded.current = true;

        if (
          (payload.employees || []).length === 0 &&
          payload.pagination?.totalPages > 0 &&
          page > payload.pagination.totalPages
        ) {
          setPage(payload.pagination.totalPages);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          addToast({ type: 'error', message: error.message });
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setFiltering(false);
        }
      }
    };

    loadEmployees();
    return () => controller.abort();
  }, [addToast, debouncedSearch, filters.area, filters.jobTitle, filters.status, page, refreshKey]);

  const updateFilter = (name, value) => {
    setFilters((currentFilters) => ({ ...currentFilters, [name]: value }));
    setPage(1);
  };

  const openAccountModal = (mode, employeeId = null) => {
    setAccountModal({ mode, employeeId });
  };

  const handleAccountSaved = () => {
    setAccountModal(null);
    setRefreshKey((currentKey) => currentKey + 1);
  };

  const handleStatusChange = async (employee, shouldBeActive) => {
    const nextStatus = shouldBeActive ? 'active' : 'inactive';
    const previousStatus = employee.status;

    setUpdatingIds((currentIds) => new Set(currentIds).add(employee.id));
    setEmployees((currentEmployees) =>
      currentEmployees.map((item) => (item.id === employee.id ? { ...item, status: nextStatus } : item))
    );

    try {
      const response = await fetchWithCsrf(`/app/master/employees/${employee.id}`, {
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: JSON.stringify({ employee: { status: nextStatus } }),
      });
      const payload = camelizeKeys(await response.json());

      if (!response.ok) {
        throw new Error(payload.errors?.join(' ') || 'Não foi possível alterar o status da conta.');
      }

      setEmployees((currentEmployees) => {
        if (filters.status && filters.status !== nextStatus) {
          return currentEmployees.filter((item) => item.id !== employee.id);
        }

        return currentEmployees.map((item) =>
          item.id === employee.id ? { ...item, ...(payload.employee || {}), status: nextStatus } : item
        );
      });
      if (filters.status && filters.status !== nextStatus) setRefreshKey((currentKey) => currentKey + 1);
      addToast({
        type: 'success',
        message: `Conta ${shouldBeActive ? 'ativada' : 'inativada'} com sucesso.`,
      });
    } catch (error) {
      setEmployees((currentEmployees) =>
        currentEmployees.map((item) => (item.id === employee.id ? { ...item, status: previousStatus } : item))
      );
      addToast({ type: 'error', message: error.message });
    } finally {
      setUpdatingIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.delete(employee.id);
        return nextIds;
      });
    }
  };

  const handleDelete = async (employee) => {
    const confirmed = await confirm({
      title: 'EXCLUIR CONTA?',
      description: `A conta de ${employee.fullName} será inativada e perderá o acesso ao sistema. Deseja continuar?`,
      confirmText: 'EXCLUIR',
    });

    if (!confirmed) return;

    setDeletingIds((currentIds) => new Set(currentIds).add(employee.id));

    try {
      const response = await fetchWithCsrf(`/app/master/employees/${employee.id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      const payload = camelizeKeys(await response.json());

      if (!response.ok) {
        throw new Error(payload.errors?.join(' ') || 'Não foi possível excluir a conta.');
      }

      setEmployees((currentEmployees) => {
        if (filters.status === 'active') {
          return currentEmployees.filter((item) => item.id !== employee.id);
        }

        return currentEmployees.map((item) =>
          item.id === employee.id ? { ...item, ...(payload.employee || {}), status: 'inactive' } : item
        );
      });

      if (filters.status === 'active') setRefreshKey((currentKey) => currentKey + 1);

      addToast({
        type: 'success',
        message: 'Conta inativada com sucesso.',
      });
    } catch (error) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setDeletingIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.delete(employee.id);
        return nextIds;
      });
    }
  };

  const areaOptions = areas.map((area) => ({ value: area, label: areaLabels[area] || area }));
  const jobTitleOptions = jobTitles.map((jobTitle) => ({
    value: jobTitle,
    label: jobTitleLabels[jobTitle] || jobTitle,
  }));
  const statusOptions = statuses.map((status) => ({ value: status, label: statusLabels[status] || status }));

  return (
    <section className="flex flex-col rounded-2xl bg-white px-6 py-7 shadow-sm">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-blueDarker">Gerenciamento de contas</h2>
          <p className="mt-1 text-sm text-blueSteel">Crie, edite e remova o acesso dos usuários da plataforma.</p>
        </div>

        <button
          type="button"
          onClick={() => openAccountModal('create')}
          className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl border-b-2 border-r-2 border-tealBase bg-tealMedium px-6 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          <Plus size={19} strokeWidth={1.8} />
          ADICIONAR CONTA
        </button>
      </div>

      <div className="mt-8 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <label className="relative block w-full xl:max-w-md">
          <span className="sr-only">Pesquisar contas</span>
          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-tealExtraDark">
            <Search size={18} strokeWidth={1.7} />
          </span>
          <input
            type="search"
            value={filters.search}
            onChange={(event) => updateFilter('search', event.target.value)}
            placeholder="Pesquisar por nome, e-mail ou CPF..."
            className="h-11 w-full rounded-full border border-tealExtraDark2 bg-white pl-11 pr-4 text-sm text-blueDark outline-none transition-colors placeholder-tealExtraDark focus:border-tealMedium focus:ring-1 focus:ring-tealMedium"
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <FilterSelect
            label="Área"
            allLabel="Todas"
            value={filters.area}
            options={areaOptions}
            onChange={(value) => updateFilter('area', value)}
          />
          <FilterSelect
            label="Cargo"
            allLabel="Todos"
            value={filters.jobTitle}
            options={jobTitleOptions}
            onChange={(value) => updateFilter('jobTitle', value)}
          />
          <FilterSelect
            label="Status"
            allLabel="Todos"
            value={filters.status}
            options={statusOptions}
            onChange={(value) => updateFilter('status', value)}
          />
          {filtering && <Loader2 className="animate-spin text-tealMedium" aria-label="Atualizando lista" size={18} />}
        </div>
      </div>

      <div className="mt-7 overflow-hidden rounded-xl border border-grayExtraLight2">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-left" style={{ minWidth: '1216px' }}>
            <colgroup>
              <col style={{ width: '96px' }} />
              <col style={{ width: '250px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '260px' }} />
              <col style={{ width: '170px' }} />
              <col style={{ width: '140px' }} />
            </colgroup>
            <thead className="bg-blueLight">
              <tr className="text-xs font-semibold text-blueSteel">
                <th className="px-6 py-4">Status</th>
                <th className="px-4 py-4">Nome</th>
                <th className="px-4 py-4">Área</th>
                <th className="px-4 py-4">Cargo</th>
                <th className="px-4 py-4">E-mail</th>
                <th className="px-4 py-4">CPF/CNPJ</th>
                <th className="px-6 py-4">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grayExtraLight2 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="7" className="h-56 text-center text-sm text-blueSteel">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="animate-spin text-tealMedium" size={19} />
                      Carregando contas...
                    </span>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan="7" className="h-48 text-center text-sm text-blueSteel">
                    Nenhuma conta encontrada para os filtros selecionados.
                  </td>
                </tr>
              ) : (
                employees.map((employee) => {
                  const active = employee.status === 'active';
                  const profile = employee.accessProfile;
                  const area = profile?.adminMaster ? 'Master' : areaLabels[profile?.area] || profile?.area || '—';
                  const jobTitle = jobTitleLabels[profile?.jobTitle] || profile?.name || '—';
                  const mutedText = active ? 'text-blueDarker' : 'text-tealExtraDark';

                  return (
                    <tr key={employee.id} className="text-sm text-blueDark transition-colors hover:bg-tealWhisper">
                      <td className="px-6 py-4">
                        <StatusToggle
                          active={active}
                          disabled={updatingIds.has(employee.id) || deletingIds.has(employee.id)}
                          employeeName={employee.fullName}
                          onChange={(nextActive) => handleStatusChange(employee, nextActive)}
                        />
                      </td>
                      <td className={`overflow-hidden px-4 py-4 font-semibold ${mutedText}`}>
                        <span className="block truncate" title={employee.fullName}>
                          {employee.fullName}
                        </span>
                      </td>
                      <td className="overflow-hidden px-4 py-4 text-blueMedium">
                        <span className="block truncate" title={area}>
                          {area}
                        </span>
                      </td>
                      <td className="overflow-hidden px-4 py-4 text-blueMedium">
                        <span className="block truncate" title={jobTitle}>
                          {jobTitle}
                        </span>
                      </td>
                      <td className={`overflow-hidden px-4 py-4 ${active ? 'text-blueSteel' : 'text-tealExtraDark'}`}>
                        <span className="block truncate" title={employee.email}>
                          {employee.email}
                        </span>
                      </td>
                      <td className={`whitespace-nowrap px-4 py-4 ${mutedText}`}>{formatDocument(employee.cpf)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            aria-label={`Visualizar conta de ${employee.fullName}`}
                            onClick={() => openAccountModal('view', employee.id)}
                            className="text-blueSteel transition-colors hover:text-blueDarker"
                          >
                            <Eye size={18} strokeWidth={1.8} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Editar conta de ${employee.fullName}`}
                            onClick={() => openAccountModal('edit', employee.id)}
                            className="text-blueSteel transition-colors hover:text-blueDarker"
                          >
                            <Pencil size={18} strokeWidth={1.8} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Excluir conta de ${employee.fullName}`}
                            disabled={!active || updatingIds.has(employee.id) || deletingIds.has(employee.id)}
                            title={active ? 'Excluir conta' : 'A conta já está inativa'}
                            onClick={() => handleDelete(employee)}
                            className="text-redMedium transition-colors hover:text-redBase disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {deletingIds.has(employee.id) ? (
                              <Loader2 className="animate-spin" size={18} strokeWidth={1.8} />
                            ) : (
                              <Trash2 size={18} strokeWidth={1.8} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} onPageChange={setPage} />
        </div>
      )}

      {accountModal && (
        <AccountModal
          mode={accountModal.mode}
          employeeId={accountModal.employeeId}
          accessProfiles={accessProfiles}
          onClose={() => setAccountModal(null)}
          onSaved={handleAccountSaved}
        />
      )}
    </section>
  );
};

export default AccountManagement;
